/**
 * Open Evidence Connect Dialog
 * Secure credential input with consent flow
 * Supports both email/password and SSO users
 */

import { FC, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
  BookOpen,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ExternalLink,
  Mail,
  KeyRound,
  ArrowRight,
  HelpCircle,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import api from '../../services/api'

interface OpenEvidenceConnectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

type Step = 'method' | 'sso-guide' | 'credentials' | 'connecting' | 'success' | 'error'

export const OpenEvidenceConnectDialog: FC<OpenEvidenceConnectDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('method')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accountHint, setAccountHint] = useState<string | null>(null)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setTimeout(() => {
        setStep('method')
        setEmail('')
        setPassword('')
        setShowPassword(false)
        setError(null)
        setAccountHint(null)
      }, 200)
    }
    onOpenChange(newOpen)
  }

  const handleConnect = async () => {
    if (!email || !password) {
      setError('Please enter your email and password')
      return
    }

    setStep('connecting')
    setError(null)

    try {
      const response = await api.post('/api/v1/integrations/open-evidence/connect', {
        email,
        password,
      })

      if (response.data.success) {
        setAccountHint(response.data.integration_info?.external_account_hint || null)
        setStep('success')
        setPassword('')
      } else {
        setError(response.data.message || 'Failed to connect')
        setStep('error')
      }
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Connection failed'
      setError(message)
      setStep('error')
    }
  }

  const handleComplete = () => {
    onSuccess()
    handleOpenChange(false)
  }

  const getHeaderInfo = () => {
    switch (step) {
      case 'success':
        return { title: 'Connected!', desc: 'Your account has been linked' }
      case 'error':
        return { title: 'Connection Failed', desc: 'Unable to connect' }
      case 'sso-guide':
        return { title: 'Set Up Password', desc: 'One-time setup for SSO users' }
      case 'credentials':
        return { title: 'Enter Credentials', desc: 'Your password is never stored' }
      default:
        return { title: 'Connect Open Evidence', desc: 'Link your NPI-verified account' }
    }
  }

  const header = getHeaderInfo()

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl border-border/50">
        {/* Header */}
        <DialogHeader className="px-6 py-5 border-b border-border/50 bg-gradient-to-br from-primary/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500',
              step === 'success'
                ? 'bg-clinical-sage/20'
                : step === 'error'
                  ? 'bg-clinical-coral/20'
                  : 'bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20'
            )}>
              {step === 'success' ? (
                <CheckCircle2 className="h-6 w-6 text-clinical-sage" />
              ) : step === 'error' ? (
                <AlertCircle className="h-6 w-6 text-clinical-coral" />
              ) : step === 'connecting' ? (
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
              ) : step === 'sso-guide' ? (
                <KeyRound className="h-6 w-6 text-primary" />
              ) : (
                <BookOpen className="h-6 w-6 text-primary" />
              )}
            </div>
            <div>
              <DialogTitle className="text-lg font-display font-semibold">
                {header.title}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {header.desc}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Choose method */}
          {step === 'method' && (
            <div className="space-y-4 animate-fade-in">
              <p className="text-sm text-muted-foreground">
                How did you sign up for Open Evidence?
              </p>

              {/* Email/Password option */}
              <button
                onClick={() => setStep('credentials')}
                className="w-full p-4 rounded-xl border border-border/50 bg-surface/50 hover:border-primary/30 hover:bg-surface transition-all duration-200 text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-clinical-teal/10 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-clinical-teal" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Email & Password</p>
                    <p className="text-xs text-muted-foreground">I created an account with my email</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>

              {/* SSO option */}
              <button
                onClick={() => setStep('sso-guide')}
                className="w-full p-4 rounded-xl border border-border/50 bg-surface/50 hover:border-primary/30 hover:bg-surface transition-all duration-200 text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-clinical-cyan/10 flex items-center justify-center">
                    <KeyRound className="h-5 w-5 text-clinical-cyan" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Google, Microsoft, or Apple</p>
                    <p className="text-xs text-muted-foreground">I signed up with a social account</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>

              {/* No account */}
              <div className="pt-2 text-center">
                <a
                  href="https://www.openevidence.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                  Don't have an account? Sign up free
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}

          {/* SSO Guide */}
          {step === 'sso-guide' && (
            <div className="space-y-4 animate-fade-in">
              <div className="rounded-xl border border-clinical-amber/30 bg-clinical-amber/5 p-4">
                <p className="text-sm font-medium text-clinical-amber mb-2">One-time setup required</p>
                <p className="text-xs text-muted-foreground">
                  To connect Casey with your Open Evidence account, you need to set a password in Open Evidence settings. This is a security feature.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-medium text-primary">1</div>
                  <div>
                    <p className="text-xs font-medium">Open your OE account settings</p>
                    <a
                      href="https://www.openevidence.com/settings"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5"
                    >
                      openevidence.com/settings
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-medium text-primary">2</div>
                  <p className="text-xs">Go to <span className="font-medium">Security</span> or <span className="font-medium">Password</span> section</p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-medium text-primary">3</div>
                  <p className="text-xs">Set a password for your account</p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-clinical-sage/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-3.5 w-3.5 text-clinical-sage" />
                  </div>
                  <p className="text-xs">Come back here and enter your email + new password</p>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-surface/80 border border-border/30">
                <Lock className="h-4 w-4 text-clinical-sage flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground">
                  Your Open Evidence password is <span className="font-medium text-clinical-sage">never stored</span> by Casey. We only keep an encrypted session token.
                </p>
              </div>
            </div>
          )}

          {/* Credentials */}
          {step === 'credentials' && (
            <div className="space-y-4 animate-fade-in">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label htmlFor="oe-email" className="text-xs font-medium text-foreground">
                    Open Evidence Email
                  </label>
                  <Input
                    id="oe-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="oe-password" className="text-xs font-medium text-foreground">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="oe-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 pr-10"
                      autoComplete="current-password"
                      onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-clinical-sage/5 border border-clinical-sage/20">
                <Lock className="h-4 w-4 text-clinical-sage flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground">
                  Your password is used once to create a session, then <span className="font-medium text-clinical-sage">immediately discarded</span>. We store only an encrypted session token that expires in 7 days.
                </p>
              </div>
            </div>
          )}

          {/* Connecting */}
          {step === 'connecting' && (
            <div className="py-8 text-center animate-fade-in">
              <div className="relative inline-flex mb-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-ping" />
              </div>
              <p className="text-sm font-medium">Connecting to Open Evidence...</p>
              <p className="text-xs text-muted-foreground mt-1">This may take a few moments</p>
            </div>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="py-4 text-center animate-fade-in">
              <div className="w-16 h-16 rounded-2xl bg-clinical-sage/20 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-clinical-sage" />
              </div>
              <p className="text-sm font-medium text-clinical-sage mb-2">Successfully Connected</p>
              {accountHint && (
                <p className="text-xs text-muted-foreground mb-4">
                  Linked account: <span className="font-medium">{accountHint}</span>
                </p>
              )}
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>Session expires in 7 days</span>
              </div>
            </div>
          )}

          {/* Error */}
          {step === 'error' && (
            <div className="py-4 animate-fade-in">
              <div className="w-16 h-16 rounded-2xl bg-clinical-coral/20 mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-clinical-coral" />
              </div>
              <p className="text-sm font-medium text-clinical-coral text-center mb-2">Connection Failed</p>
              <p className="text-xs text-muted-foreground text-center mb-4">
                {error || 'Unable to connect to Open Evidence'}
              </p>

              <div className="rounded-lg bg-clinical-coral/5 border border-clinical-coral/20 p-3">
                <p className="text-[11px] font-medium text-foreground mb-1.5">Troubleshooting:</p>
                <ul className="space-y-1 text-[11px] text-muted-foreground">
                  <li>• Verify your email and password are correct</li>
                  <li>• Try logging in at openevidence.com first</li>
                  <li>• If you use Google/Apple sign-in, <button onClick={() => setStep('sso-guide')} className="text-primary hover:underline">set up a password</button></li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border/50 bg-surface/30">
          {step === 'method' && (
            <Button variant="ghost" onClick={() => handleOpenChange(false)} className="text-xs">
              Cancel
            </Button>
          )}

          {step === 'sso-guide' && (
            <>
              <Button variant="ghost" onClick={() => setStep('method')} className="text-xs">
                Back
              </Button>
              <Button onClick={() => setStep('credentials')} className="text-xs">
                I've Set a Password
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </>
          )}

          {step === 'credentials' && (
            <>
              <Button variant="ghost" onClick={() => setStep('method')} className="text-xs">
                Back
              </Button>
              <Button onClick={handleConnect} disabled={!email || !password} className="text-xs">
                <Lock className="h-3.5 w-3.5 mr-1.5" />
                Connect Account
              </Button>
            </>
          )}

          {step === 'connecting' && (
            <Button variant="ghost" disabled className="text-xs">
              Connecting...
            </Button>
          )}

          {step === 'success' && (
            <Button onClick={handleComplete} className="text-xs w-full bg-clinical-sage hover:bg-clinical-sage/90">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              Done
            </Button>
          )}

          {step === 'error' && (
            <>
              <Button variant="ghost" onClick={() => handleOpenChange(false)} className="text-xs">
                Cancel
              </Button>
              <Button onClick={() => setStep('credentials')} className="text-xs">
                Try Again
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


